import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CreditCard, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import { SimpleModal, errorText } from './SimpleModal';
import { PaymentTabs } from './CheckoutPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-');
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('subscriberToken')}` });

const Row = ({ label, value, testId }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.55rem 0', borderBottom: '1px solid #edf2f7', fontSize: '0.92rem' }}>
    <span style={{ color: '#718096' }}>{label}</span>
    <strong data-testid={testId} style={{ color: '#2D3748', textAlign: 'right' }}>{value}</strong>
  </div>
);

const PixActions = ({ sub, onChanged, setMsg }) => {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancelar a assinatura no Pix? Você não receberá novas cobranças e o acesso continua até ${fmtDate(sub.subscription_end)}.`)) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API}/digital/subscriber/my-subscription/cancel`, {}, { headers: authHeaders() }); setMsg(data.message); onChanged(); } catch (e) { setMsg(errorText(e, 'Não foi possível cancelar agora.')); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
      <Button variant="outline" onClick={cancel} disabled={busy} data-testid="my-sub-cancel-pix-btn" style={{ width: '100%', color: '#c53030', borderColor: '#feb2b2' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar assinatura (Pix)'}
      </Button>
    </div>
  );
};

const CardActions = ({ sub, color, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [swap, setSwap] = useState(false);
  const [msg, setMsg] = useState('');
  if (sub.pix_recurring) return <>{msg && <p data-testid="my-sub-message" style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '0.75rem' }}>{msg}</p>}<PixActions sub={sub} onChanged={onChanged} setMsg={setMsg} /></>;
  if (!sub.auto_renew) return msg ? <p data-testid="my-sub-message" style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '0.75rem' }}>{msg}</p> : null;
  const cancel = async () => {
    if (!window.confirm('Cancelar a assinatura no cartão? Não haverá novas cobranças e seu acesso continua até o fim do mês já pago.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/digital/subscriber/my-subscription/cancel`, {}, { headers: authHeaders() });
      setMsg(data.message);
      onChanged();
    } catch (e) {
      setMsg(errorText(e, 'Não foi possível cancelar agora.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
      {msg && <p data-testid="my-sub-message" style={{ fontSize: '0.85rem', color: '#4a5568' }}>{msg}</p>}
      <Button variant="outline" onClick={() => setSwap((v) => !v)} data-testid="my-sub-swap-card-btn" style={{ width: '100%' }}>
        {swap ? 'Fechar troca de cartão' : 'Trocar cartão'}
      </Button>
      {swap && (
        <div data-testid="my-sub-swap-card" style={{ borderTop: '1px solid #edf2f7', paddingTop: '0.75rem' }}>
          <p style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.5rem' }}>O novo cartão substitui o atual. Nada é cobrado agora: a próxima mensalidade sai no fim do período já pago.</p>
          <PaymentTabs cardOnly editorId={sub.editor_id} token={localStorage.getItem('subscriberToken')} amount={sub.valor_mensal} payerEmail={sub.email}
            color={color} onPaid={onChanged} onDone={() => { setSwap(false); onChanged(); }} />
        </div>
      )}
      <Button variant="outline" onClick={cancel} disabled={busy} data-testid="my-sub-cancel-btn" style={{ width: '100%', color: '#c53030', borderColor: '#feb2b2' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar assinatura'}
      </Button>
    </div>
  );
};

const Renew = ({ sub, color, initialTab, onChanged, onClose }) => (
  <div data-testid="my-sub-renew" style={{ marginTop: '1rem', borderTop: '1px solid #edf2f7', paddingTop: '1rem' }}>
    {(sub.needs_resubscribe || sub.renewal_status === 'reauthorization_required') && (
      <p data-testid="my-sub-resubscribe-note" style={{ fontSize: '0.82rem', color: '#975a16', background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 8, padding: '0.5rem 0.7rem', marginBottom: '0.75rem' }}>
        Mudamos a forma de pagamento. Seu acesso pago vai até {fmtDate(sub.subscription_end)}; cadastre o cartão abaixo para renovar sozinho todo mês, ou pague com Pix.
      </p>
    )}
    <p style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.75rem' }}>No cartão, a mensalidade renova sozinha e começa a ser cobrada quando seu acesso atual terminar. No Pix, a mensalidade também segue até você cancelar: pague o mês agora (somado à validade atual) e o Pix dos próximos meses chega por e-mail.</p>
    <PaymentTabs editorId={sub.editor_id} token={localStorage.getItem('subscriberToken')} amount={sub.valor_mensal} payerEmail={sub.email}
      editorName={sub.editora} color={color} initialTab={initialTab} onPaid={onChanged} onDone={() => { onChanged(); onClose?.(); }} />
  </div>
);

export const MySubscriptionModal = ({ onClose, color = '#68AAB0', initialTab = 'card' }) => {
  const [sub, setSub] = useState(null);
  const [error, setError] = useState('');
  const load = () => axios.get(`${API}/digital/subscriber/my-subscription`, { headers: authHeaders() })
    .then((r) => setSub(r.data)).catch((e) => setError(errorText(e, 'Não foi possível carregar sua assinatura.')));
  useEffect(() => { load(); }, []);

  const free = sub && (sub.plataforma_publica || sub.isento);
  const method = sub?.payment_method === 'card' ? <><CreditCard className="w-4 h-4 inline mr-1" />{sub.auto_renew ? `Cartão${sub.card_last_digits ? ` final ${sub.card_last_digits}` : ''} (mensal automático)` : 'Cartão'}</> : sub?.payment_method === 'pix' ? (sub.pix_recurring ? 'Pix (mensal, até cancelar)' : 'Pix (cancelada)') : '-';
  return (
    <SimpleModal title="Minha assinatura" onClose={onClose} testId="my-subscription-modal" maxWidth={560}>
      {error && <p data-testid="my-sub-error" style={{ color: '#c53030' }}>{error}</p>}
      {!sub && !error && <Loader2 className="w-6 h-6 animate-spin" />}
      {sub && (
        <>
          <Row label="Catálogo" value={sub.editora || '-'} testId="my-sub-editora" />
          <Row label="Situação" value={sub.status === 'active' ? 'Ativa' : sub.status} testId="my-sub-status" />
          {free ? <Row label="Acesso" value={sub.isento ? 'Isento de pagamento' : 'Biblioteca gratuita'} testId="my-sub-free" /> : (
            <>
              <Row label="Forma de pagamento" value={method} testId="my-sub-method" />
              <Row label="Valor mensal" value={BRL(sub.valor_mensal)} testId="my-sub-price" />
              <Row label="Acesso válido até" value={<><CalendarClock className="w-4 h-4 inline mr-1" />{fmtDate(sub.subscription_end)}</>} testId="my-sub-end" />
              {sub.auto_renew && sub.next_charge_date && <Row label="Próxima cobrança no cartão" value={fmtDate(`${sub.next_charge_date}T12:00:00`)} testId="my-sub-next-charge" />}
            </>
          )}
          {!free && <CardActions sub={sub} color={color} onChanged={load} />}
          {!free && !sub.auto_renew && <Renew sub={sub} color={color} initialTab={initialTab} onChanged={load} onClose={onClose} />}
        </>
      )}
    </SimpleModal>
  );
};
